import React from 'react';
import { ActivityType } from '../../types/itinerary';
import { 
  Landmark, Coffee, Mountain, Music, ShoppingBag, 
  UtensilsCrossed, Moon, Palmtree 
} from 'lucide-react';

interface InterestSelectorProps {
  selected: ActivityType[];
  onChange: (types: ActivityType[]) => void;
}

const INTERESTS: Array<{
  type: ActivityType;
  label: string;
  icon: React.ReactNode;
  activeColor: string;
  activeBg: string;
}> = [
  { 
    type: 'Cultural',
    label: 'Cultural',
    icon: <Landmark size={16} />,
    activeColor: 'text-purple-700',
    activeBg: 'bg-purple-100'
  },
  { 
    type: 'Adventure',
    label: 'Adventure',
    icon: <Mountain size={16} />,
    activeColor: 'text-blue-700',
    activeBg: 'bg-blue-100'
  },
  { 
    type: 'Foodie',
    label: 'Food & Dining',
    icon: <UtensilsCrossed size={16} />,
    activeColor: 'text-orange-700',
    activeBg: 'bg-orange-100'
  },
  { 
    type: 'Nightlife',
    label: 'Nightlife',
    icon: <Moon size={16} />,
    activeColor: 'text-indigo-700',
    activeBg: 'bg-indigo-100'
  },
  { 
    type: 'Nature',
    label: 'Nature',
    icon: <Palmtree size={16} />,
    activeColor: 'text-green-700',
    activeBg: 'bg-green-100'
  },
  { 
    type: 'Shopping',
    label: 'Shopping',
    icon: <ShoppingBag size={16} />,
    activeColor: 'text-pink-700',
    activeBg: 'bg-pink-100'
  },
  { 
    type: 'Relaxation',
    label: 'Relaxation',
    icon: <Coffee size={16} />,
    activeColor: 'text-teal-700',
    activeBg: 'bg-teal-100'
  }
];

const textStyles = {
  // Base text styles
  base: {
    fontFamily: 'var(--chat-font-family)',
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-normal)',
    lineHeight: 'var(--chat-line-height)',
    color: 'var(--planner-text-primary)'
  },
  // Header styles
  h2: {
    fontSize: 'var(--planner-heading-size)',
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  h3: {
    fontSize: 'var(--planner-subheading-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  h4: {
    fontSize: 'var(--planner-body-size)',
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  // Text sizes
  large: {
    fontSize: 'var(--planner-subheading-size)'
  },
  small: {
    fontSize: 'var(--planner-small-size)'
  },
  xs: {
    fontSize: 'calc(var(--chat-font-size) * 0.833)' // 10px if base is 12px
  },
  // Font weights
  medium: {
    fontWeight: 'var(--planner-font-weight-medium)'
  },
  semibold: {
    fontWeight: 'var(--planner-font-weight-semibold)'
  },
  // Colors
  secondary: {
    color: 'var(--planner-text-secondary)'
  },
  tertiary: {
    color: 'var(--planner-text-tertiary)'
  }
};
  
export function InterestSelector({ selected, onChange }: InterestSelectorProps) {
  const toggleInterest = (type: ActivityType) => {
    if (selected.includes(type)) {
      onChange(selected.filter(t => t !== type));
    } else {
      onChange([...selected, type]);
    }
  };

  return (
    <div className="space-y-4">
      <label 
        className="block"
        style={{
          ...textStyles.base,
          ...textStyles.regular,
          ...textStyles.regular,
          marginBottom: '0.5rem'
        }}
      >
        What interests you?
      </label>
      <div className="flex flex-wrap gap-4">
        {INTERESTS.map(({ type, label, icon, activeColor, activeBg }) => {
          const isSelected = selected.includes(type);
          return (
            <button
              key={type}
              onClick={() => toggleInterest(type)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                isSelected 
                  ? `${activeBg} ${activeColor}` 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={{
                ...textStyles.base,
                ...textStyles.medium,
                ...textStyles.medium,
                color: isSelected ? undefined : 'var(--planner-text-secondary)'
              }}
            >
              <span 
                className={isSelected ? activeColor : 'text-gray-500'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {React.cloneElement(icon as React.ReactElement, {
                  style: { 
                    width: 'var(--planner-icon-size-md)', 
                    height: 'var(--planner-icon-size-md)' 
                  }
                })}
              </span>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}